import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, UserCog, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EditarUsuarioModal } from "@/components/usuarios/EditarUsuarioModal";
import { ConfirmacionAdminModal } from "@/components/dashboard/ConfirmacionAdminModal";

interface Usuario {
  id: string;
  user_id: string;
  nombre: string;
  email: string;
  rol: string;
  created_at: string;
  roles_asignados?: { role: string }[];
}

const GestionUsuarios = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<Usuario | null>(null);
  const [modalEditarOpen, setModalEditarOpen] = useState(false);
  const [confirmEliminarOpen, setConfirmEliminarOpen] = useState(false);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState<Usuario | null>(null);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      
      // Obtener perfiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Obtener roles de cada usuario
      const usuariosConRoles = await Promise.all(
        (profiles || []).map(async (profile) => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', profile.user_id);

          return {
            ...profile,
            roles_asignados: roles || []
          };
        })
      );

      setUsuarios(usuariosConRoles);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los usuarios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const handleEditarUsuario = (usuario: Usuario) => {
    setUsuarioSeleccionado(usuario);
    setModalEditarOpen(true);
  };

  const handleSolicitarEliminar = (usuario: Usuario) => {
    setUsuarioAEliminar(usuario);
    setConfirmEliminarOpen(true);
  };

  const handleConfirmarEliminar = async () => {
    if (!usuarioAEliminar) return;

    try {
      // Eliminar roles del usuario
      const { error: rolesError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', usuarioAEliminar.user_id);

      if (rolesError) throw rolesError;

      // Eliminar perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', usuarioAEliminar.user_id);

      if (profileError) throw profileError;

      toast({
        title: "Usuario eliminado",
        description: "El usuario ha sido eliminado correctamente",
      });

      setConfirmEliminarOpen(false);
      setUsuarioAEliminar(null);
      cargarUsuarios();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el usuario",
        variant: "destructive",
      });
    }
  };

  const getRolColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500';
      case 'moderator':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card/95 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/configuraciones')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Gestión de Usuarios</h1>
              <p className="text-sm text-muted-foreground">Administrar usuarios y permisos del sistema</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Usuarios del Sistema</CardTitle>
            <CardDescription>
              Lista de todos los usuarios registrados y sus roles asignados
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Roles Asignados</TableHead>
                    <TableHead>Fecha de Registro</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usuarios.map((usuario) => (
                    <TableRow key={usuario.id}>
                      <TableCell className="font-medium">{usuario.nombre || 'Sin nombre'}</TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {usuario.roles_asignados && usuario.roles_asignados.length > 0 ? (
                            usuario.roles_asignados.map((r, idx) => (
                              <Badge key={idx} className={getRolColor(r.role)}>
                                {r.role}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline">sin rol</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(usuario.created_at).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditarUsuario(usuario)}
                          >
                            <UserCog className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleSolicitarEliminar(usuario)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <EditarUsuarioModal
        usuario={usuarioSeleccionado}
        open={modalEditarOpen}
        onOpenChange={setModalEditarOpen}
        onUsuarioActualizado={cargarUsuarios}
      />

      <ConfirmacionAdminModal
        open={confirmEliminarOpen}
        onOpenChange={setConfirmEliminarOpen}
        onConfirm={handleConfirmarEliminar}
        titulo="Eliminar Usuario"
        descripcion={`¿Estás seguro de que deseas eliminar al usuario ${usuarioAEliminar?.nombre}? Esta acción no se puede deshacer.`}
      />
    </div>
  );
};

export default GestionUsuarios;
